/**
 * DateRangePicker — inline date range picker using native HTML date inputs.
 * Zero extra dependencies. Toggles a dropdown panel with start/end inputs.
 */
import React from 'react';
import { Calendar, X } from 'lucide-react';

interface DateRange {
  start: string;
  end: string;
}

interface DateRangePickerProps {
  value: DateRange | null;
  onChange: (range: DateRange | null) => void;
}

function formatDate(dateStr: string): string {
  if (!dateStr) return '';
  const d = new Date(dateStr + 'T00:00:00');
  return d.toLocaleDateString('en-US', { month: '2-digit', day: '2-digit', year: 'numeric' });
}

export function DateRangePicker({ value, onChange }: DateRangePickerProps) {
  const [open, setOpen] = React.useState(false);
  const [localStart, setLocalStart] = React.useState(value?.start ?? '');
  const [localEnd, setLocalEnd] = React.useState(value?.end ?? '');
  const ref = React.useRef<HTMLDivElement>(null);

  React.useEffect(() => {
    setLocalStart(value?.start ?? '');
    setLocalEnd(value?.end ?? '');
  }, [value]);

  React.useEffect(() => {
    function handleClick(e: MouseEvent) {
      if (ref.current && !ref.current.contains(e.target as Node)) setOpen(false);
    }
    document.addEventListener('mousedown', handleClick);
    return () => document.removeEventListener('mousedown', handleClick);
  }, []);

  const hasDates = !!(localStart && localEnd);

  const handleApply = () => {
    if (localStart && localEnd) {
      onChange({ start: localStart, end: localEnd });
      setOpen(false);
    }
  };

  const handleClear = () => {
    setLocalStart('');
    setLocalEnd('');
    onChange(null);
  };

  const displayValue = value && value.start && value.end
    ? `${formatDate(value.start)} - ${formatDate(value.end)}`
    : null;

  return (
    <div ref={ref} className="relative inline-flex items-center gap-2">
      <button
        type="button"
        onClick={() => setOpen(!open)}
        className={`inline-flex items-center gap-2 px-3 py-2 text-sm rounded-full border transition-colors min-w-[220px] ${
          open
            ? 'bg-disney-blue-500 text-white border-disney-blue-500'
            : displayValue
              ? 'bg-white dark:bg-surface-dark-card text-gray-700 dark:text-gray-300 border-gray-300 dark:border-gray-600 hover:border-gray-400'
              : 'bg-white dark:bg-surface-dark-card text-gray-400 dark:text-gray-300 border-gray-300 dark:border-gray-600 hover:border-gray-400'
        }`}
        aria-haspopup="listbox"
        aria-expanded={open}
      >
        <Calendar className="w-4 h-4" />
        <span>{displayValue ?? 'Select date range...'}</span>
      </button>

      {displayValue && (
        <button
          onClick={handleClear}
          className="p-1.5 rounded-full text-gray-400 hover:text-gray-600 dark:hover:text-gray-200 hover:bg-gray-100 dark:hover:bg-gray-800 transition-colors"
          aria-label="Clear date range"
          title="Clear date range"
        >
          <X className="w-4 h-4" />
        </button>
      )}

      {open && (
        <div
          className="absolute top-full left-0 mt-1 bg-white dark:bg-surface-dark-card border border-gray-200 dark:border-gray-700 rounded-lg shadow-lg z-50 p-4 min-w-[300px]"
          role="listbox"
        >
          <div className="space-y-3">
            <div>
              <label htmlFor="drp-start" className="block text-xs font-medium text-gray-500 dark:text-gray-400 mb-1">
                Start date
              </label>
              <input
                id="drp-start"
                type="date"
                value={localStart}
                onChange={(e) => setLocalStart(e.target.value)}
                className="w-full px-3 py-2 text-sm rounded-md border border-gray-300 dark:border-gray-600 bg-white dark:bg-surface-dark-card text-gray-900 dark:text-gray-100 focus:border-disney-blue-500 focus:outline-none focus:ring-1 focus:ring-disney-blue-500"
              />
            </div>
            <div>
              <label htmlFor="drp-end" className="block text-xs font-medium text-gray-500 dark:text-gray-400 mb-1">
                End date
              </label>
              <input
                id="drp-end"
                type="date"
                value={localEnd}
                onChange={(e) => setLocalEnd(e.target.value)}
                className="w-full px-3 py-2 text-sm rounded-md border border-gray-300 dark:border-gray-600 bg-white dark:bg-surface-dark-card text-gray-900 dark:text-gray-100 focus:border-disney-blue-500 focus:outline-none focus:ring-1 focus:ring-disney-blue-500"
              />
            </div>
            <div className="flex items-center gap-2 pt-1">
              <button
                onClick={handleApply}
                disabled={!hasDates}
                className="flex-1 px-3 py-1.5 text-sm font-medium text-white bg-disney-blue-500 rounded-md hover:bg-disney-blue-600 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
              >
                Apply
              </button>
              {hasDates && (
                <button
                  onClick={handleClear}
                  className="px-3 py-1.5 text-sm text-gray-600 dark:text-gray-400 hover:text-gray-800 dark:hover:text-gray-200 transition-colors"
                  aria-label="Clear date range"
                  title="Clear date range"
                >
                  <X className="w-4 h-4" />
                </button>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
